/*
 * Add-to-cart for the curated upsell cards.
 *
 * Deliberately not Dawn's <product-form>: on the cart page that would pop the
 * "added to cart" notification over a cart the customer is already looking at,
 * and leave the list underneath stale until a reload. This updates whichever
 * surface the card sits on instead — the cart sections in place on the cart
 * page, the notification itself when the card is inside the popup.
 */
if (!customElements.get('cart-upsell-form')) {
  customElements.define(
    'cart-upsell-form',
    class CartUpsellForm extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        if (!this.form) return;

        this.button = this.querySelector('[type="submit"]');
        this.spinner = this.querySelector('.cart-upsell__spinner');
        this.errorElement = this.querySelector('.cart-upsell__error');
        this.select = this.querySelector('[data-variant-select]');

        this.form.addEventListener('submit', this.onSubmit.bind(this));
        if (this.select) this.select.addEventListener('change', this.syncPrice.bind(this));
      }

      // Each variant carries its own price, so the card must not keep showing
      // the first variant's price after the customer picks another colour.
      syncPrice() {
        const priceElement = this.querySelector('[data-price]');
        const option = this.select.selectedOptions[0];
        if (priceElement && option && option.dataset.price) {
          priceElement.textContent = option.dataset.price;
        }
      }

      get notification() {
        return document.querySelector('cart-notification');
      }

      get onCartPage() {
        return Boolean(document.getElementById('main-cart-items'));
      }

      cartPageSections() {
        const items = document.getElementById('main-cart-items');
        const footer = document.getElementById('main-cart-footer');
        const upsell = document.getElementById('cart-upsell');

        const sections = [
          { id: 'main-cart-items', section: items.dataset.id, selector: '.js-contents' },
          { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
        ];
        if (footer) {
          sections.push({ id: 'main-cart-footer', section: footer.dataset.id, selector: '.js-contents' });
        }
        // Captured before the fetch: once the last suggestion is added the
        // section renders empty and #cart-upsell is no longer in the document.
        if (upsell && upsell.dataset.sectionId) {
          sections.push({
            id: `shopify-section-${upsell.dataset.sectionId}`,
            section: upsell.dataset.sectionId,
            selector: '.shopify-section',
          });
        }
        return sections;
      }

      onSubmit(event) {
        event.preventDefault();
        if (this.button.getAttribute('aria-disabled') === 'true') return;

        this.setLoading(true);
        this.showError('');

        const formData = new FormData(this.form);
        const sections = this.onCartPage
          ? this.cartPageSections().map((section) => section.section)
          : (this.notification ? this.notification.getSectionsToRender().map((section) => section.id) : []);

        if (sections.length) {
          formData.append('sections', sections.join(','));
          formData.append('sections_url', window.location.pathname);
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((parsed) => {
            if (parsed.status) {
              this.showError(parsed.description || parsed.message);
              return;
            }

            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'cart-upsell',
                productVariantId: formData.get('id'),
                cartData: parsed,
              });
            }

            if (this.onCartPage) {
              this.renderCartPage(parsed);
            } else if (this.notification) {
              this.notification.renderContents(parsed);
            } else {
              window.location = routes.cart_url;
            }
          })
          .catch((error) => {
            console.error(error);
            this.showError();
          })
          .finally(() => this.setLoading(false));
      }

      renderCartPage(parsed) {
        this.cartPageSections().forEach((section) => {
          const target = document.getElementById(section.id);
          const html = parsed.sections && parsed.sections[section.section];
          if (!target || !html) return;

          const source = new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector);
          target.innerHTML = source ? source.innerHTML : '';
        });
      }

      setLoading(loading) {
        this.button.setAttribute('aria-disabled', loading ? 'true' : 'false');
        this.button.classList.toggle('loading', loading);
        if (this.spinner) this.spinner.classList.toggle('hidden', !loading);
      }

      showError(message) {
        if (!this.errorElement) return;
        if (message === '') {
          this.errorElement.hidden = true;
          this.errorElement.textContent = '';
          return;
        }
        this.errorElement.textContent = message || window.cartUpsellStrings?.error || '';
        this.errorElement.hidden = !this.errorElement.textContent;
      }
    }
  );
}
